import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

const CONTACTO = "socifymkt@gmail.com · +34 653 74 76 30";

const DOCS = {
  "aviso-legal": {
    title: "Aviso Legal",
    updated: "Última actualización: 2026",
    sections: [
      {
        h: "1. Titular del sitio web",
        p: [
          "En cumplimiento de la Ley 34/2002, de Servicios de la Sociedad de la Información y de Comercio Electrónico (LSSI-CE), se informa de que el titular de este sitio web es Abraham Carpio — Socify Marketing, con domicilio en España.",
          `Contacto: ${CONTACTO}. NIE: Y0447650S.`,
        ],
      },
      {
        h: "2. Objeto",
        p: [
          "Este sitio web tiene carácter informativo y comercial: presenta los servicios de marketing digital de Socify Marketing (tiendas online, diseño gráfico, gestión de redes sociales, Google Business y páginas web) y facilita el contacto directo con el titular a través de formulario y WhatsApp.",
          "El acceso y uso del sitio atribuye la condición de usuario e implica la aceptación de las presentes condiciones.",
        ],
      },
      {
        h: "3. Propiedad intelectual",
        p: [
          "Todos los contenidos del sitio (textos, diseños, logotipos, imágenes y trabajos mostrados en el portfolio) son titularidad de Socify Marketing o de sus respectivos clientes, y están protegidos por la normativa de propiedad intelectual.",
          "Queda prohibida su reproducción, distribución o comunicación pública sin autorización expresa del titular.",
        ],
      },
      {
        h: "4. Responsabilidad",
        p: [
          "El titular no se hace responsable de los daños derivados del uso indebido de los contenidos ni de los fallos técnicos ajenos a su control.",
          "Los enlaces a servicios de terceros (WhatsApp, Instagram, Google) se rigen por las políticas y condiciones de dichas plataformas.",
        ],
      },
      {
        h: "5. Legislación aplicable",
        p: [
          "Estas condiciones se rigen por la legislación española. Para cualquier controversia serán competentes los juzgados y tribunales del domicilio del consumidor o usuario.",
        ],
      },
    ],
  },
  privacidad: {
    title: "Política de Privacidad",
    updated: "Última actualización: 2026",
    sections: [
      {
        h: "1. Responsable del tratamiento",
        p: [
          "Responsable: Abraham Carpio — Socify Marketing.",
          `Contacto: ${CONTACTO}.`,
        ],
      },
      {
        h: "2. Datos que recogemos",
        p: [
          "A través del formulario de contacto recogemos: nombre, teléfono, correo electrónico, servicio de interés, presupuesto aproximado y el mensaje que nos envíes.",
          "No recogemos categorías especiales de datos ni datos de menores de edad de forma deliberada.",
        ],
      },
      {
        h: "3. Finalidad y base jurídica",
        p: [
          "Utilizamos tus datos exclusivamente para gestionar tu solicitud, contactar contigo y enviarte un presupuesto de nuestros servicios.",
          "La base jurídica del tratamiento es tu consentimiento expreso (art. 6.1.a del RGPD), otorgado al enviar el formulario.",
        ],
      },
      {
        h: "4. Conservación y destinatarios",
        p: [
          "Conservamos tus datos mientras dure la gestión de tu solicitud y, en su caso, la relación comercial, o hasta que solicites su supresión.",
          "No cedemos tus datos a terceros. Los proveedores de alojamiento web y de correo electrónico actúan como encargados del tratamiento bajo las garantías adecuadas.",
        ],
      },
      {
        h: "5. Tus derechos",
        p: [
          "Puedes ejercer tus derechos de acceso, rectificación, supresión, oposición, limitación del tratamiento y portabilidad escribiendo a socifymkt@gmail.com, adjuntando un documento que acredite tu identidad.",
          "Si consideras que el tratamiento no se ajusta a la normativa, puedes presentar una reclamación ante la Agencia Española de Protección de Datos (AEPD).",
        ],
      },
    ],
  },
  cookies: {
    title: "Política de Cookies",
    updated: "Última actualización: 2026",
    sections: [
      {
        h: "1. ¿Qué son las cookies?",
        p: [
          "Las cookies son pequeños archivos que se almacenan en tu dispositivo al visitar un sitio web y que permiten, entre otras cosas, recordar preferencias y analizar el uso del sitio.",
        ],
      },
      {
        h: "2. Cookies que utiliza esta web",
        p: [
          "Cookies técnicas: imprescindibles para el funcionamiento y la seguridad del sitio. No requieren tu consentimiento.",
          "Cookies de análisis: nos ayudan a conocer de forma agregada y anónima cómo se usa la web (páginas visitadas, tiempo de navegación) para mejorarla.",
        ],
      },
      {
        h: "3. Cómo desactivarlas",
        p: [
          "Puedes configurar tu navegador para bloquear o eliminar las cookies: Chrome (Configuración > Privacidad y seguridad), Firefox (Ajustes > Privacidad y seguridad), Safari (Preferencias > Privacidad) y Edge (Configuración > Cookies y permisos del sitio).",
          "Ten en cuenta que bloquear las cookies técnicas puede afectar al funcionamiento del sitio.",
        ],
      },
      {
        h: "4. Actualizaciones",
        p: [
          "Esta política puede actualizarse para adaptarse a cambios normativos o técnicos. Te recomendamos revisarla periódicamente.",
        ],
      },
    ],
  },
};

export default function Legal({ doc }) {
  const d = DOCS[doc] || DOCS["aviso-legal"];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [doc]);

  return (
    <div data-testid={`legal-page-${doc}`} className="min-h-screen bg-[#FAF9F6]">
      <header className="border-b border-slate-200 bg-white/70 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-5">
          <span className="font-display text-lg font-extrabold tracking-tight text-slate-900">
            SOCIFY<span className="text-[#1D4ED8]">.</span>
          </span>
          <a
            data-testid="legal-back-link"
            href="/"
            className="group flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-xs font-bold text-slate-700 transition-colors hover:bg-slate-900 hover:text-white"
          >
            <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" />
            Volver a la web
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16 sm:py-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="mb-3 font-mono-sig text-xs font-bold uppercase tracking-[0.3em] text-[#1D4ED8]">
            {d.updated}
          </p>
          <h1 className="mb-12 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
            {d.title}
          </h1>

          <div className="space-y-10">
            {d.sections.map((s) => (
              <section key={s.h}>
                <h2 className="mb-3 font-display text-xl font-bold text-slate-900">{s.h}</h2>
                {s.p.map((par, i) => (
                  <p key={i} className="mb-2 text-base leading-relaxed text-slate-600">
                    {par}
                  </p>
                ))}
              </section>
            ))}
          </div>

          <p className="mt-16 border-t border-slate-200 pt-8 text-sm text-slate-500">
            ¿Dudas sobre este documento? Escríbenos a{" "}
            <a href="mailto:socifymkt@gmail.com" className="font-bold text-[#1D4ED8] hover:underline">
              socifymkt@gmail.com
            </a>
          </p>
        </motion.div>
      </main>
    </div>
  );
}
